package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.{ErrorSearchData, LogbackEventData}
import com.neurobs.ccab.db.{ErrorClassData, ErrorData, ErrorTypeData}
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.AdminPanelController
import com.neurobs.js.view.{Gui, ValueSelectBox}
import com.neurobs.js.util.StringUtilities.dateTimeString
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.{ElementFactory, InputTextBox, RecordTableView, SelectSelectBox}
import com.neurobs.util.JsonPrinter
import com.neurobs.errors._
import com.neurobs.ccabserver.administrator.controller.TableNavigationController._

import scala.concurrent.ExecutionContext.Implicits.global
import scala.concurrent.{Future, Promise}
import scala.collection.mutable.ArrayBuffer
import scalatags.JsDom.all._
import io.circe.generic.auto._
import org.scalajs.dom.raw.HTMLElement

object ErrorListController {

	case class CustomData
	(
		errorClassId: Option[Long] = None,
		errorTypeId: Option[Long] = None,
		contentContains: Option[String] = None
	)

	class ErrorSearchForm(cache: RecordCache) extends SearchForm[CustomData] {
		private val contentContainsBox = InputTextBox(makeTextInput(width = 30))

		private val inputClassBox = SelectSelectBox(makeSelectBox(width = 20))
		private val classBox = ValueSelectBox(inputClassBox, cache.errorClasses.get, (i: ErrorClassData) => i.description, (i: ErrorClassData) => i.id, true)

		private val inputTypeBox = SelectSelectBox(makeSelectBox(width = 20))
		private val typeBox = ValueSelectBox(inputTypeBox, cache.errorTypes.get, (i: ErrorTypeData) => i.description, (i: ErrorTypeData) => i.id, true)

		override def container: HTMLElement = {
			val temp = ElementFactory.makeFormTable(Array(
				("Class:", inputClassBox.select),
				("Type:", inputTypeBox.select),
				("Content contains:", contentContainsBox.text)
			))
			temp.style.margin = "0"
			temp
		}

		override def set(s: CustomData): Unit = {
			classBox.setSelectedValue(s.errorClassId)
			typeBox.setSelectedValue(s.errorTypeId)
			contentContainsBox.setValue(s.contentContains.getOrElse(""))
		}

		override def get: Result[CustomData] = Right(CustomData(
			errorClassId = classBox.value,
			errorTypeId = typeBox.value,
			contentContains = if (contentContainsBox.value().isEmpty) None else Some(contentContainsBox.value())
		))

		override def clear(): Unit = {
			inputClassBox.setSelected(None)
			inputTypeBox.setSelected(None)
			contentContainsBox.setValue("")
		}
	}

	class ErrorAdapter(gui: Gui, cache: RecordCache) extends Adapter[CustomData, ErrorData] {
		override def getId(d: ErrorData): Long = d.id

		override def get(search: NavigationSearchData[CustomData]): Future[Result[Seq[ErrorData]]] = {
			val temp = ErrorSearchData(minTime = search.minTime, maxTime = search.maxTime, errorClassId = search.data.errorClassId, errorTypeId = search.data.errorTypeId, contentContains = search.data.contentContains, maxId = search.maxId, limit = search.limit)
			val p = Promise[Result[Seq[ErrorData]]]
			CCABServer.searchErrors(temp) {
				case Left(error) => p.success(Left(error.message))
				case Right(data) => p.success(Right(data))
			}
			p.future
		}

		override def columnHeaders: Seq[RecordTableView.ColumnHeader] = { Seq(
			"DID",
			"Time",
			"Class",
			"Type",
			"Content",
			"Content Type"
		)}

		def recordRow(a: ErrorData): Seq[RecordTableView.CellContent] = {
			val classElement = span().render
			a.errorClassId match {
				case None => classElement.innerHTML = a.`class`
				case Some(id) => cache.errorClasses.getRecord(_.id == id) map {
					case Right(data) => classElement.innerHTML = data.map(_.description).getOrElse(a.`class`)
					case _ =>
				}
			}
			val typeElement = span().render
			a.errorTypeId match {
				case None => typeElement.innerHTML = a.`type`
				case Some(id) =>
					cache.errorTypes.getRecord(_.id == id) map {
						case Right(data) => typeElement.innerHTML = data.map(_.description).getOrElse(a.`type`)
						case _ =>
					}
			}

			val logbackData = JsonPrinter.fromJson[LogbackEventData](a.content)
			val contentElement = logbackData match {
				case None => span(a.content).render
				case Some(logbackData) =>
					val rows = ArrayBuffer[(HTMLElement, HTMLElement)](
						("log", logbackData.log),
						("msg", pre(logbackData.msg.trim).render),
						("level", logbackData.level)
					)
					logbackData.arg0.foreach(a => rows.append(("arg0", a)))
					logbackData.arg1.foreach(a => rows.append(("arg1", a)))
					logbackData.arg2.foreach(a => rows.append(("arg2", a)))
					logbackData.arg3.foreach(a => rows.append(("arg3", a)))
					logbackData.exc.foreach(e => rows.append(("exception", pre(e.trim).render)))
					val table = ElementFactory.makeParameterTable(rows.toArray, useBorders = true)
					import com.neurobs.js.dom.DOMViewElement._
					val link = ElementFactory.makeLink(text = " (details)")
					link.onclick = _ => {
						gui.showModalMessage(width = 0, title = s"Logback error ${a.id}", description = "", dismissButtonCaption = "Continue", customContent = Some(table))(_ => Unit)
					}
					span(logbackData.msg, link).render
			}

			Seq(
				a.id.toString,
				dateTimeString(a.eventTime),
				classElement,
				typeElement,
				contentElement,
				a.contentType
			)
		}

		def createSearchForm: SearchForm[CustomData] = new ErrorSearchForm(cache)
	}

	def apply(gui: Gui, cache: RecordCache): AdminPanelController = {
		val adapter = new ErrorAdapter(gui, cache)
		new TableNavigationPanelController(title = "Main Error List", adapter)
	}
}

// This allows creation of errors for testing
//		val createClassBox = InputTextBox(makeTextInput(width = 20))
//		val createTypeBox = InputTextBox(makeTextInput(width = 20))
//		val createContentBox = InputTextBox(makeTextInput(width = 20))
//		val createContentTypeBox = InputTextBox(makeTextInput(width = 20))
//		val createButton = makeButton(caption = "Create").render
//		createButton.onclick = (_) => {
//			val data = ErrorCreateData(eventTime = SecondsTimestamp.now(), `class` = createClassBox.value(), `type` = createTypeBox.value(), content = createContentBox.value(), contentType = createContentTypeBox.value())
//			CCABServer.createError(data) {
//				case Left(error) => setError(error.message)
//				case _ =>
//			}
//		}
//		val temp = ElementFactory.makeFormTable(Array(
//			("Class:", createClassBox.text),
//			("Type:", createTypeBox.text),
//			("Content:", createContentBox.text),
//			("Content type:", createContentTypeBox.text)
//		))
//		grid.addRow(temp)
//		grid.addRow(createButton)